import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/getMember';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Gets member information for a member.
 * @param {object} logger
 */
export const GetMember = () => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Fetching member data.`, { correlation: { transactionId } });

        // step 2: await Web service call
        const getMemberQuery = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
                'Accept-Language': language,
            },
            body: JSON.stringify({
                operationName: 'GetMemberQuery',
                variable: {},
                query: `
                    query GetMemberQuery {
                        member {
                            firstName
                            lastName
                            birthDate
                            effectiveDate
                            clientNumber
                            certificateNumber
                            postalAddress {
                                addressId
                                suite
                                status
                                type
                                streetAddress
                                city
                                postalOrZipCode
                                country
                                phoneNumber
                                stateOrProvince
                            }
                            emailAddress
                            bankAccount {
                                bankAccountId
                                bankName
                                branchNumber
                                institutionNumber
                                accountNumber
                                status
                                usage
                            }
                            plan {
                                childDependentMaxAge
                                studentMaxAge
                            }
                        }
                    }
                `,
            }),
        });

        const getMemberQueryResponse = await getMemberQuery.json();

        if (getMemberQueryResponse.errors) {
            const { message, code } = getMemberQueryResponse.errors[0];
            logger.critical(
                `${ACTION_NAME_PREFIX}: Failure when fetching member data. Code: ${code} Reason: ${message}`,
                { correlation: { transactionId } }
            );
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }
        return dispatch({ type: RESPONSE_ACTION_NAME, data: getMemberQueryResponse.data.member, success: true });
    };
};

export const requestReducer = produce((state) => {
    state.MemberData.isLoadingMemberData = true;
});

export const responseReducer = produce((state, action) => {
    state.MemberData.isLoadingMemberData = false;

    // Members can only use this application to view and manage bank accounts used for
    // Health/Dental claims. If returned bank account is for anything else, it is not relevant
    // for this application.
    const bankAccount = action.data.bankAccount?.usage !== 'HEALTH_AND_DENTAL_CLAIMS' ? null : action.data.bankAccount;

    state.MemberData.member = { ...action.data, bankAccount };

    if (bankAccount) {
        state.BankData.bankName = bankAccount.bankName;
    }
});

/**
 * Fired off when the request encouters an error.
 * An error thrown by this part of the code is considered a critical failure.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isLoadingMemberData = false;
    state.Errors.initialLoad.hasError = true;
    state.Errors.initialLoad.code = action.code;
    state.Errors.initialLoad.message = action.message;
});

registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
